import React from "react";
import { Box, Paper, Typography, useMediaQuery } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import { BRAND_COLOR } from "@kivunova/kivufrontendcommon";

export interface InventoryPageHeaderProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  backAction?: {
    label: string;
    onClick: () => void;
  };
}

const InventoryPageHeader: React.FC<InventoryPageHeaderProps> = ({
  title,
  subtitle,
  actions,
  backAction
}) => {
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 1.5,
        py: { xs: 1, md: 1.25 },
        px: { xs: 1.25, md: 1.5 },
        borderRadius: 2,
        border: `1px solid ${alpha(BRAND_COLOR, 0.1)}`,
        background: `linear-gradient(135deg, ${alpha(BRAND_COLOR, 0.04)} 0%, ${alpha(BRAND_COLOR, 0.01)} 100%)`
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        flexDirection={matchMD ? "column" : "row"}
        alignItems={matchMD ? "stretch" : "center"}
        gap={matchMD ? 1.25 : 1}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{
              letterSpacing: "-0.02em",
              fontSize: { xs: "1.125rem", md: "1.25rem" },
              lineHeight: 1.25
            }}
          >
            {title}
          </Typography>
          {subtitle ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.25, fontSize: "0.75rem", lineHeight: 1.35 }}
            >
              {subtitle}
            </Typography>
          ) : null}
        </Box>

        {(actions || backAction) && (
          <Box
            display="flex"
            flexWrap="wrap"
            justifyContent={matchMD ? "center" : "flex-end"}
            alignItems="center"
            gap={1}
          >
            {actions}
            {backAction ? (
              <Box
                component="button"
                type="button"
                onClick={backAction.onClick}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  backgroundColor: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: BRAND_COLOR,
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  "&:hover": { bgcolor: alpha(BRAND_COLOR, 0.06) }
                }}
              >
                <ArrowBackOutlinedIcon fontSize="small" />
                {backAction.label}
              </Box>
            ) : null}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default InventoryPageHeader;
